import getUserIdFromToken from 'helpers/methods/getUserIdFromToken'

const COOKIE_CONSENT_STORAGE_KEY = `${process.env.REACT_APP_BASE_STORAGE_KEY}_cookie_consent`
export const COOKIE_CONSENT_POLICY_VERSION = '2026-09-14'
// This COOKIE_CONSENT_VALIDITY_MS constant is used to calculate expiresAt.
const COOKIE_CONSENT_VALIDITY_DAYS = 180
const COOKIE_CONSENT_VALIDITY_MS = COOKIE_CONSENT_VALIDITY_DAYS * 24 * 60 * 60 * 1000

export const COOKIE_CONSENT_CHOICES = Object.freeze({
    accepted: 'accepted',
    refused: 'refused',
    customized: 'customized',
})

export const DEFAULT_COOKIE_CONSENT_PREFERENCES = Object.freeze({
    essential: true,
    googleAnalytics: false,
    hotjar: false,
})

const REFUSED_COOKIE_CONSENT_PREFERENCES = Object.freeze({
    essential: true,
    googleAnalytics: false,
    hotjar: false,
})

/**
 * getAllAcceptedPreferences
 * @returns {object} Cookie preferences with all categories accepted
 */
function getAllAcceptedPreferences() {
    return {
        essential: true,
        googleAnalytics: true,
        hotjar: false,
    }
}

/**
 * normalizeCookieConsentPreferences
 * @param {string} choice Cookie consent choice
 * @param {object=} preferences Cookie consent preferences
 * @returns {object} Normalized cookie consent preferences
 */
export function normalizeCookieConsentPreferences(choice, preferences = DEFAULT_COOKIE_CONSENT_PREFERENCES) {
    if (choice === COOKIE_CONSENT_CHOICES.accepted)
        return getAllAcceptedPreferences()

    if (choice === COOKIE_CONSENT_CHOICES.refused)
        return { ...REFUSED_COOKIE_CONSENT_PREFERENCES }

    return {
        ...DEFAULT_COOKIE_CONSENT_PREFERENCES,
        googleAnalytics: !!preferences?.googleAnalytics,
        hotjar: false,
    }
}

/**
 * hasValidCookieConsentPreferences
 * @param {object} preferences Cookie consent preferences
 * @returns {boolean} Has every expected preference key
 */
function hasValidCookieConsentPreferences(preferences) {
    return typeof preferences?.essential === 'boolean'
        && typeof preferences?.googleAnalytics === 'boolean'
        && typeof preferences?.hotjar === 'boolean'
}

/**
 * isCookieConsentOwnedByCurrentUser
 * A consent with no userId was made anonymously and can be adopted by whoever logs in next.
 * When nobody is authenticated, this browser's last valid consent still applies regardless of
 * which account set it: logging out of the app is not a withdrawal of cookie consent for the
 * device. A consent tagged with a *different* authenticated user's id, however, must never be
 * reused: it must not be displayed as "no popup needed" nor synchronized to that user's database
 * record — this is the one case that must stay strict (a shared/kiosk browser switching accounts).
 * @param {{userId?: (number|null)}} consent Stored consent
 * @returns {boolean} Is consent usable for the current session
 */
function isCookieConsentOwnedByCurrentUser(consent) {
    if (consent.userId === undefined || consent.userId === null)
        return true

    const currentUserId = getUserIdFromToken()

    if (currentUserId === null)
        return true

    return consent.userId === currentUserId
}

/**
 * getLocalStorage
 * @returns {Storage|null} Browser local storage
 */
function getLocalStorage() {
    if (typeof window === 'undefined')
        return null

    return window.localStorage
}

/**
 * getConsentExpiresAt
 * @param {object} consent Stored consent
 * @returns {Date|null} Consent expiration date
 */
function getConsentExpiresAt(consent) {
    const storedExpiresAt = new Date(consent.expiresAt)

    if (!Number.isNaN(storedExpiresAt.getTime()))
        return storedExpiresAt

    const createdAt = new Date(consent.createdAt)

    if (Number.isNaN(createdAt.getTime()))
        return null

    return new Date(createdAt.getTime() + COOKIE_CONSENT_VALIDITY_MS)
}

/**
 * getConsentUpdatedAt
 * @param {object} consent Stored consent
 * @returns {string|null} Consent update date
 */
function getConsentUpdatedAt(consent) {
    const storedUpdatedAt = new Date(consent.updatedAt)

    if (!Number.isNaN(storedUpdatedAt.getTime()))
        return storedUpdatedAt.toISOString()

    const createdAt = new Date(consent.createdAt)

    if (!Number.isNaN(createdAt.getTime()))
        return createdAt.toISOString()

    return null
}

/**
 * @typedef {object} StoredCookieConsent
 * @property {string} choice Cookie consent choice
 * @property {(number|null)} userId Consent owner (userId), or null when made anonymously
 * @property {string} createdAt Creation date (ISO string)
 * @property {string} updatedAt Last update date (ISO string)
 * @property {Date} expiresAt Expiration date
 * @property {object} preferences Cookie consent preferences
 * @property {string} policyVersion Cookie consent policy version
 */

/**
 * getStoredCookieConsent
 * @returns {StoredCookieConsent|null} Stored cookie consent
 */
export function getStoredCookieConsent() {
    const storage = getLocalStorage()

    if (!storage)
        return null

    try {
        const storedConsent = storage.getItem(COOKIE_CONSENT_STORAGE_KEY)

        if (!storedConsent)
            return null

        const consent = JSON.parse(storedConsent)
        const expiresAt = getConsentExpiresAt(consent)
        const updatedAt = getConsentUpdatedAt(consent)

        if (
            !Object.values(COOKIE_CONSENT_CHOICES).includes(consent.choice)
            || consent.policyVersion !== COOKIE_CONSENT_POLICY_VERSION
            || !expiresAt
            || !updatedAt
        ) {
            storage.removeItem(COOKIE_CONSENT_STORAGE_KEY)
            return null
        }

        if (expiresAt.getTime() <= Date.now()) {
            storage.removeItem(COOKIE_CONSENT_STORAGE_KEY)
            return null
        }

        // A consent tagged with another user's id (a previous session on a shared browser) must never
        // be reused for the current session: neither displayed as "already consented" nor synchronized
        // to the current user's account in DB.
        if (!isCookieConsentOwnedByCurrentUser(consent)) {
            storage.removeItem(COOKIE_CONSENT_STORAGE_KEY)
            return null
        }

        const preferences = normalizeCookieConsentPreferences(consent.choice, consent.preferences)

        if (!hasValidCookieConsentPreferences(consent.preferences)
            || JSON.stringify(consent.preferences) !== JSON.stringify(preferences)
            || consent.updatedAt !== updatedAt)
            storage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify({
                ...consent,
                preferences,
                updatedAt,
            }))

        return {
            choice: consent.choice,
            userId: consent.userId ?? null,
            createdAt: consent.createdAt,
            updatedAt,
            expiresAt,
            preferences,
            policyVersion: consent.policyVersion,
        }
    } catch (error) {
        storage.removeItem(COOKIE_CONSENT_STORAGE_KEY)
        return null
    }
}

/**
 * getStoredCookieConsentChoice
 * @returns {string|null} Stored cookie consent choice
 */
export function getStoredCookieConsentChoice() {
    return getStoredCookieConsent()?.choice ?? null
}

/**
 * getStoredCookieConsentPreferences
 * @returns {object} Stored cookie consent preferences
 */
export function getStoredCookieConsentPreferences() {
    return getStoredCookieConsent()?.preferences ?? { ...DEFAULT_COOKIE_CONSENT_PREFERENCES }
}

/**
 * getStoredCookieConsentExpiresAt
 * @returns {Date|null} Stored cookie consent expiration date
 */
export function getStoredCookieConsentExpiresAt() {
    return getStoredCookieConsent()?.expiresAt ?? null
}

/**
 * @typedef {object} CookieConsentInput
 * @property {string} choice Cookie consent choice
 * @property {(number|null)} [userId] Consent owner (userId), or null/omitted for the current session
 * @property {object} [preferences] Cookie consent preferences
 * @property {string} [policyVersion] Cookie consent policy version
 * @property {string} [createdAt] Creation date (ISO string)
 * @property {string} [updatedAt] Last update date (ISO string)
 * @property {string} [expiresAt] Expiration date (ISO string)
 */

/**
 * saveCookieConsent
 * @param {CookieConsentInput} cookieConsent Cookie consent data
 * @returns {StoredCookieConsent} Stored cookie consent
 */
export function saveCookieConsent(cookieConsent) {
    const now = new Date()
    const createdAt = cookieConsent.createdAt || now.toISOString()
    const updatedAt = cookieConsent.updatedAt || now.toISOString()
    const expiresAt = cookieConsent.expiresAt || new Date(now.getTime() + COOKIE_CONSENT_VALIDITY_MS).toISOString()
    const normalizedPreferences = normalizeCookieConsentPreferences(cookieConsent.choice, cookieConsent.preferences)
    // Tag the stored consent with its owner so it can never be mistaken for another user's choice
    // (see isCookieConsentOwnedByCurrentUser). Defaults to the currently authenticated user, or null
    // when no one is logged in (anonymous consent, adoptable by whoever logs in next).
    const userId = cookieConsent.userId !== undefined ? cookieConsent.userId : getUserIdFromToken()
    const consent = {
        choice: cookieConsent.choice,
        userId,
        preferences: normalizedPreferences,
        policyVersion: cookieConsent.policyVersion || COOKIE_CONSENT_POLICY_VERSION,
        createdAt,
        updatedAt,
        expiresAt: new Date(expiresAt).toISOString(),
    }
    const storage = getLocalStorage()

    if (storage)
        storage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent))

    return {
        ...consent,
        expiresAt: new Date(consent.expiresAt),
    }
}

/**
 * saveCookieConsentChoice
 * @param {string} choice Cookie consent choice
 * @param {object=} preferences Cookie consent preferences
 * @returns {StoredCookieConsent} Stored cookie consent
 */
export function saveCookieConsentChoice(choice, preferences = DEFAULT_COOKIE_CONSENT_PREFERENCES) {
    const now = new Date()

    return saveCookieConsent({
        choice,
        // Tag with whoever is authenticated right now (or null if anonymous): this choice was just
        // made in the current session, so it always belongs to it.
        userId: getUserIdFromToken(),
        preferences,
        policyVersion: COOKIE_CONSENT_POLICY_VERSION,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        expiresAt: new Date(now.getTime() + COOKIE_CONSENT_VALIDITY_MS).toISOString(),
    })
}
